import loggingService from 'common/logging/loggingService';
import AsyncButton from 'common/ui/AsyncButton';
import ErrorList from 'common/ui/ErrorList';
import ValidationError from 'common/validationError';
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { CreatePartnerIntegrationRequest } from './partnerIntegrationService';
import EditAuthCredentials from './EditAuthCredentials';
import { getPartnerName, Partner } from 'features/settings/importMapping/partner';
import { AgTraxConfigurationSettings } from './agTraxConfigurationSettings';
import { AgrisConfigurationSettings } from './agrisConfigurationSettings';
import { AgvanceConfigurationSettings } from './agvanceConfigurationSettings';
import { AgVantageConfigurationSettings } from './agVantageConfigurationSettings';
import { AgTechConfigurationSettings } from './agTechConfigurationSettings';
import NumberEditor from 'common/ui/NumberEditor';
import { EverAgConfigurationSettings } from './everAgConfigurationSettings';
import { IntellegoConfigurationSettings } from './intellegoConfigurationSettings';


export interface ConfigurePartnerIntegrationModalProps {
    partner: Partner;
    showAuthCredentials: boolean;
    showExternalId: boolean;
    externalIdLabel: string;
    onSave(request: CreatePartnerIntegrationRequest): Promise<any>;
    onHide(): void;
}

function getBlankRequest(): CreatePartnerIntegrationRequest {
    return {
        partner: null,
        externalId: '',
        authenticationKey: '',
        configuration: {}
    };
}

const ConfigurePartnerIntegrationModal: React.FC<ConfigurePartnerIntegrationModalProps> = props => {

    const [request, setRequest] = useState<CreatePartnerIntegrationRequest>(getBlankRequest);
    const [isValidRequest, setIsValidRequest] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
        const newRequest = {...getBlankRequest(), partner: props.partner};

        setRequest(newRequest);
    }, [props.partner]);

    useEffect(() => {
        setIsValidRequest(isValid());
    }, [request]);

    function isValid(): boolean {
        if ((props.showExternalId && request.externalId.length === 0) || 
            (props.showAuthCredentials && request.authenticationKey.length === 0)) return false;
        
        if(props.partner === Partner.AgVantage
            && (
                !request.configuration?.publicApiKey?.trim() ||
                !request.configuration?.privateApiKey?.trim() ||
                !request.configuration?.baseUrl?.trim()
            )
        )
            return false;

        if(props.partner === Partner.Intellego && !request.configuration?.databaseConnectionString?.trim())
            return false;
        return true;
    }

    async function onSave() {
        try {
            await props.onSave(request);
            setRequest(getBlankRequest());
            setErrors([]);
            props.onHide();
        }
        catch (err) {
            loggingService.error("Unable to create partner integration", err as Error);
            const validationError = err as ValidationError;
            setErrors(validationError.errors ?? ["Unable to create partner integration"]);
        }
    }

    return <Modal show onHide={props.onHide} backdrop="static" size="lg">
        <Modal.Header closeButton>
            <Modal.Title>
                Configure {getPartnerName(props.partner)}
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>

            {props.showExternalId && <div>
                <Form.Label>{props.externalIdLabel}</Form.Label>
                <Form.Control value={request.externalId}
                    onChange={ev => setRequest({...request, externalId: ev.target.value})}
                />
            </div>}

            {props.showAuthCredentials &&
            <div>
                <Form.Label>Auth Credentials</Form.Label>
                <EditAuthCredentials 
                    partner={props.partner} 
                    authenticationKey={request.authenticationKey}
                    onChange={val => setRequest({...request, authenticationKey: val})} />
            </div>}

            <OtherConfiguration partner={props.partner}
                configuration={request.configuration}
                onConfigurationChange={val => setRequest({...request, configuration: val})} />

            <ErrorList errors={errors} />

        </Modal.Body>
        <Modal.Footer>
            <div className="pull-right">
                <Button variant="secondary" className="ml-2" onClick={props.onHide}>
                    Cancel
                </Button>&nbsp;
                <AsyncButton variant="primary" onClickAsync={onSave} disabled={!isValidRequest}>
                    Create
                </AsyncButton>
            </div>
        </Modal.Footer>
    </Modal>;
}

interface OtherConfigurationProps<T = any> {
    partner: Partner;
    configuration: T;
    onConfigurationChange(config: T): void;
}
const OtherConfiguration: React.FC<OtherConfigurationProps> = props => {

    switch(props.partner) {
        case Partner.AgTrax:
            return <AgTraxConfiguration {...props} />;
        case Partner.Agris:
            return <AgrisConfiguration {...props} />;
        case Partner.Agvance:
            return <AgvanceConfiguration {...props} />;
        case Partner.EverAg:
            return <EverAgConfiguration {...props} />;
        case Partner.AgVantage:
            return <AgVantageConfiguration {...props} />;
        case Partner.AgTech:
            return <AgTechConfiguration {...props} />;
        case Partner.Intellego:
            return <IntellegoConfiguration {...props} />;
            
        default:
            return <></>;
    }
}

const AgTraxConfiguration: React.FC<OtherConfigurationProps<AgTraxConfigurationSettings>> = props => {
    
    return <div>
        <Form.Label>AgTrax API URL</Form.Label>
        <Form.Control value={props.configuration.agTraxUrl}
            onChange={ev => props.onConfigurationChange({...props.configuration, agTraxUrl: ev.target.value})}
        />
    </div>
}

const AgrisConfiguration: React.FC<OtherConfigurationProps<AgrisConfigurationSettings>> = props => {
    
    return <div>
        <Form.Label>Data Set</Form.Label>
        <Form.Control value={props.configuration.dataSet}
            placeholder="001"
            onChange={ev => props.onConfigurationChange({...props.configuration, dataSet: ev.target.value})}
        />

        <Form.Check
            type="switch"
            id="enable-proxy-switch"
            label="Use Proxy"
            checked={props.configuration.useProxy}
            onChange={ev => props.onConfigurationChange({...props.configuration, useProxy: ev.target.checked})}
        />
        {!props.configuration.useProxy && <>
            <Form.Label>Agris API URL</Form.Label>
            <Form.Control value={props.configuration.url}
                placeholder="https://"
                onChange={ev => props.onConfigurationChange({...props.configuration, url: ev.target.value})}
            />
        </>}
        
        {props.configuration.useProxy && <>
            <Form.Label>Proxy Route Identifier</Form.Label>
            <Form.Control value={props.configuration.proxyRouteIdentifier}
                placeholder="route name"
                onChange={ev => props.onConfigurationChange({...props.configuration, proxyRouteIdentifier: ev.target.value})}
            />
        </>}
    </div>
}

const AgvanceConfiguration: React.FC<OtherConfigurationProps<AgvanceConfigurationSettings>> = props => {
    
    return <div>
        <Form.Label>API Key</Form.Label>
        <Form.Control value={props.configuration.apiKey}
            onChange={ev => props.onConfigurationChange({...props.configuration, apiKey: ev.target.value})}
        />
    </div>
}

const EverAgConfiguration: React.FC<OtherConfigurationProps<EverAgConfigurationSettings>> = props => {
    
    return <div>
        <Form.Label>Subscription Key</Form.Label>
        <Form.Control value={props.configuration.apiKey}
            onChange={ev => props.onConfigurationChange({...props.configuration, apiKey: ev.target.value})}
        />
    </div>
}

const AgVantageConfiguration: React.FC<OtherConfigurationProps<AgVantageConfigurationSettings>> = props => {
    
    return <div>
        <Form.Label>Public Api Key</Form.Label>
        <Form.Control 
            value={props.configuration.publicApiKey}
            onChange={ev => props.onConfigurationChange({...props.configuration, publicApiKey: ev.target.value})}
        />
        
        <Form.Label>Private Api Key</Form.Label>
        <Form.Control 
            value={props.configuration.privateApiKey}
            onChange={ev => props.onConfigurationChange({...props.configuration, privateApiKey: ev.target.value})}
        />

        <Form.Label>Base API URL</Form.Label>
        <Form.Control 
            value={props.configuration.baseUrl}
            placeholder="https://"
            onChange={ev => props.onConfigurationChange({...props.configuration, baseUrl: ev.target.value})}
        />
    </div>
}

const AgTechConfiguration: React.FC<OtherConfigurationProps<AgTechConfigurationSettings>> = props => {
    
    return <div>
        <Form.Label>FTP URL</Form.Label>
        <Form.Control 
            value={props.configuration.ftpAddress}
            onChange={ev => props.onConfigurationChange({...props.configuration, ftpAddress: ev.target.value})}
        />
        <Form.Label>Port</Form.Label>
        <NumberEditor
            value={props.configuration.port ?? 21}
            min={0}
            onValueChange={val => props.onConfigurationChange({...props.configuration, port: val})}
        />

        <Form.Check
            type="switch"
            id="use-sftp"
            label="Use SFTP"
            checked={props.configuration.useSftp}
            onChange={ev => props.onConfigurationChange({ ...props.configuration, useSftp: ev.target.checked })}
        />
        
        <Form.Label>Input Folder</Form.Label>
        <Form.Control 
            value={props.configuration.inputFolder}
            onChange={ev => props.onConfigurationChange({...props.configuration, inputFolder: ev.target.value})}
        />

        <Form.Label>Output Folder</Form.Label>
        <Form.Control 
            value={props.configuration.outputFolder}
            onChange={ev => props.onConfigurationChange({...props.configuration, outputFolder: ev.target.value})}
        />
    </div>
}

const IntellegoConfiguration: React.FC<OtherConfigurationProps<IntellegoConfigurationSettings>> = props => {
    
    return <div>
        <Form.Label>Database Connection String</Form.Label>
        <Form.Control 
            value={props.configuration.databaseConnectionString}
            onChange={ev => props.onConfigurationChange({...props.configuration, databaseConnectionString: ev.target.value})}
        />
    </div>
}


export default ConfigurePartnerIntegrationModal;